import { http, HttpResponse, passthrough, type HttpHandler } from 'msw';

import {
  mockGeocodeResults,
  mockPlaceDetails,
  mockPlacePredictions,
} from '@/mocks/data/googlePlacesData';

/**
 * Google Maps JS API requests are JSONP: the response must call the `callback` given in the query string.
 */
const jsonp = (request: Request, payload: unknown) => {
  const callback = new URL(request.url).searchParams.get('callback');
  return new HttpResponse(
    callback
      ? `/**/${callback} && ${callback}(${JSON.stringify(payload)})`
      : '',
    { headers: { 'Content-Type': 'text/javascript; charset=UTF-8' } }
  );
};

/**
 * Third-party calls made by the Google Maps JS API (Places autocomplete/details, Geocoder), Stripe, reCAPTCHA, CopilotKit and Datadog.
 * The JS bundles of these libraries are not mocked (see `passthroughHandlers`), only their data calls.
 */
export const thirdPartyHandlers: HttpHandler[] = [
  // Google Places (react-google-autocomplete: AutocompleteService + PlacesService)
  http.get(
    'https://maps.googleapis.com/maps/api/place/js/AutocompletionService.GetPredictionsJson',
    ({ request }) => jsonp(request, mockPlacePredictions)
  ),
  http.get(
    'https://maps.googleapis.com/maps/api/place/js/PlaceService.GetPlaceDetails',
    ({ request }) => jsonp(request, mockPlaceDetails)
  ),
  // Google Geocoder (asset details map)
  http.get(
    'https://maps.googleapis.com/maps/api/js/GeocodeService.Search',
    ({ request }) => jsonp(request, mockGeocodeResults)
  ),
  // Maps JS API key check (the real one rejects the localhost referrer, which makes the Places autocomplete unusable)
  http.get(
    'https://maps.googleapis.com/maps/api/js/AuthenticationService.Authenticate',
    ({ request }) => jsonp(request, [1, null, 0, null, null, [1]])
  ),
  http.get(
    'https://maps.googleapis.com/maps/api/mapsjs/gen_204',
    () => new HttpResponse(null, { status: 204 })
  ),

  // Stripe fraud-detection telemetry
  http.post('https://m.stripe.com/6', () => HttpResponse.json({})),
  http.post('https://r.stripe.com/b', () => HttpResponse.json({})),

  // CopilotKit telemetry and notifications
  http.get('https://api.cloud.copilotkit.ai/ciu', () => HttpResponse.json({})),
  http.post('https://telemetry.copilotkit.ai/ingest', () =>
    HttpResponse.json({})
  ),
  http.get('https://cdn.copilotkit.ai/notifications/v1.json', () =>
    HttpResponse.json({ notifications: [] })
  ),

  // reCAPTCHA client-side logging (fired when the "I'm not a robot" checkbox is clicked)
  http.post(
    'https://www.google.com/recaptcha/api2/clr',
    () => new HttpResponse(null, { status: 204 })
  ),

  // Datadog telemetry
  http.all(
    'https://*.datadoghq.com/*',
    () => new HttpResponse(null, { status: 200 })
  ),
  http.all(
    'https://*.browser-intake-datadoghq.com/*',
    () => new HttpResponse(null, { status: 200 })
  ),
];

/**
 * Static assets that are fetched from the real network instead of being mocked: favicons, fonts, and the
 * scripts/styles/iframes of third-party libraries (Google Maps, Stripe, reCAPTCHA). There is no data in them to fake.
 *
 * - Must stay at the END of `handlers`: a request is only passed through if no mock before it matched.
 * - Defined as handlers (rather than through `onUnhandledRequest`) so every `setupWorker`/`setupServer` shares them.
 */
export const passthroughHandlers: HttpHandler[] = [
  // Favicons: the app's own (served from `public/`) and the ones of the helpful links' domains (Favicon.tsx)
  http.get('*/favicon.ico', () => passthrough()),
  http.get('https://icons.duckduckgo.com/ip3/*', () => passthrough()),
  // Google Fonts
  http.get('https://fonts.googleapis.com/*', () => passthrough()),
  http.get('https://fonts.gstatic.com/*', () => passthrough()),
  // Google Maps JS API (loader script and its chunks)
  http.get('https://maps.googleapis.com/maps/api/js', () => passthrough()),
  http.get('https://maps.googleapis.com/maps-api-v3/*', () => passthrough()),
  http.get('https://maps.gstatic.com/*', () => passthrough()),
  // Stripe.js and its iframes
  http.get('https://js.stripe.com/*', () => passthrough()),
  http.get('https://m.stripe.network/*', () => passthrough()),
  // reCAPTCHA (script, challenge iframes, and the reload/userverify calls that validate the checkbox)
  http.get('https://www.google.com/recaptcha/*', () => passthrough()),
  http.post('https://www.google.com/recaptcha/*', () => passthrough()),
  http.get('https://www.gstatic.com/recaptcha/*', () => passthrough()),
  // Placeholder avatars (AccountLinkingPage stories)
  http.get('https://i.pravatar.cc/*', () => passthrough()),
];
